import {
  doc,
  getDoc,
  setDoc,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { normalizeMemberGroup, withMemberGroup } from '../utils/memberGroupPaths';

type AssignmentMemoEntry = {
  studentId: string;
  studentName?: string;
  studentNumber?: string;
  memo: string;
  updatedAt: Date;
};

type AssignmentMemoDocument = {
  classId: string;
  lastUpdated: Date;
  assignments: {
    [assignmentKey: string]: {
      [studentId: string]: AssignmentMemoEntry;
    };
  };
};

const ASSIGNMENT_MEMO_DOC_ID = 'assignmentMemo-1';

export const assignmentMemoService = {
  /** 세션 내 유지 — 저장·삭제·명시적 무효화 시에만 갱신 */
  __memoCache: new Map<string, AssignmentMemoDocument>(),

  __resolveMemberGroup(userId: string, classId: string, memberGroup?: string) {
    if (memberGroup) {
      return normalizeMemberGroup(memberGroup);
    }
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const key = `selected_member_group_${userId}_${classId}`;
        const saved = window.localStorage.getItem(key);
        if (saved) {
          return normalizeMemberGroup(saved);
        }
      }
    } catch {
      // ignore
    }
    return 'A';
  },

  __getCollectionId(userId: string, classId: string, memberGroup?: string) {
    const group = this.__resolveMemberGroup(userId, classId, memberGroup);
    return withMemberGroup('assignmentMemo', group);
  },

  __getCacheKey(userId: string, classId: string, memberGroup?: string) {
    const group = this.__resolveMemberGroup(userId, classId, memberGroup);
    return `assignmentMemo_${userId}_${classId}_${group}`;
  },

  /** 메모·과제 저장 후 또는 멤버 그룹 전환 시 호출 */
  invalidateMemoCache(userId: string, classId: string, memberGroup?: string) {
    this.__memoCache.delete(this.__getCacheKey(userId, classId, memberGroup));
  },

  __setMemoCacheEntry(
    docData: AssignmentMemoDocument,
    assignmentKey: string,
    studentId: string,
    entry: AssignmentMemoEntry
  ) {
    if (!docData.assignments[assignmentKey]) {
      docData.assignments[assignmentKey] = {};
    }
    docData.assignments[assignmentKey][studentId] = entry;
    docData.lastUpdated = new Date();
  },

  __deleteMemoCacheEntry(docData: AssignmentMemoDocument, assignmentKey: string, studentId: string) {
    const bucket = docData.assignments[assignmentKey];
    if (!bucket) return;
    delete bucket[studentId];
    if (Object.keys(bucket).length === 0) {
      delete docData.assignments[assignmentKey];
    }
    docData.lastUpdated = new Date();
  },

  async loadMemoDocument(
    userId: string,
    classId: string,
    memberGroup?: string,
    forceRefresh: boolean = false
  ): Promise<AssignmentMemoDocument> {
    const cacheKey = this.__getCacheKey(userId, classId, memberGroup);
    const cached = this.__memoCache.get(cacheKey);
    if (!forceRefresh && cached) {
      return cached;
    }

    const collectionId = this.__getCollectionId(userId, classId, memberGroup);
    const memoRef = doc(db, 'users', userId, 'classes', classId, collectionId, ASSIGNMENT_MEMO_DOC_ID);
    const memoSnap = await getDoc(memoRef);
    const data = memoSnap.exists()
      ? (memoSnap.data() as any)
      : { assignments: {}, classId, lastUpdated: new Date() };

    const normalized: AssignmentMemoDocument = {
      classId: data.classId || classId,
      lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : (data.lastUpdated || new Date()),
      assignments: data.assignments || {}
    };

    this.__memoCache.set(cacheKey, normalized);
    return normalized;
  },

  async getMemo(
    userId: string,
    classId: string,
    assignmentKey: string,
    studentId: string,
    memberGroup?: string
  ): Promise<string> {
    const docData = await this.loadMemoDocument(userId, classId, memberGroup);
    return docData.assignments?.[assignmentKey]?.[studentId]?.memo || '';
  },

  async getMemoMapForAssignment(
    userId: string,
    classId: string,
    assignmentKey: string,
    memberGroup?: string
  ): Promise<Record<string, AssignmentMemoEntry>> {
    const docData = await this.loadMemoDocument(userId, classId, memberGroup);
    return docData.assignments?.[assignmentKey] || {};
  },

  /**
   * assignmentMemo 컬렉션에서 특정 학생의 메모 삭제
   */
  async deleteMemo(
    userId: string,
    classId: string,
    assignmentKey: string,
    studentId: string,
    memberGroup?: string
  ): Promise<void> {
    const key = (assignmentKey || '').trim();
    const sid = (studentId || '').trim();
    if (!key || !sid) {
      return;
    }
    const collectionId = this.__getCollectionId(userId, classId, memberGroup);
    const memoRef = doc(db, 'users', userId, 'classes', classId, collectionId, ASSIGNMENT_MEMO_DOC_ID);
    // 중첩 구조만 한 번에 병합 (플랫 키 assignments.x.y 와 동시 쓰기는 일부 환경에서 400 유발)
    await setDoc(
      memoRef,
      {
        classId,
        lastUpdated: new Date(),
        assignments: {
          [key]: {
            [sid]: deleteField()
          }
        }
      },
      { merge: true }
    );

    const cached = this.__memoCache.get(this.__getCacheKey(userId, classId, memberGroup));
    if (cached) {
      this.__deleteMemoCacheEntry(cached, key, sid);
    }
  },

  async saveMemo(
    userId: string,
    classId: string,
    assignmentKey: string,
    studentId: string,
    memo: string,
    studentName?: string,
    studentNumber?: string,
    memberGroup?: string
  ): Promise<void> {
    // 빈 메모는 삭제
    if (!memo || !memo.trim()) {
      await this.deleteMemo(userId, classId, assignmentKey, studentId, memberGroup);
      return;
    }
    
    const collectionId = this.__getCollectionId(userId, classId, memberGroup);
    const memoRef = doc(db, 'users', userId, 'classes', classId, collectionId, ASSIGNMENT_MEMO_DOC_ID);
    const entry: AssignmentMemoEntry = {
      studentId,
      memo: memo.trim(),
      updatedAt: new Date(),
      ...(studentName ? { studentName } : {}),
      ...(studentNumber ? { studentNumber } : {})
    };

    await setDoc(
      memoRef,
      {
        classId,
        lastUpdated: new Date(),
        assignments: {
          [assignmentKey]: {
            [studentId]: entry
          }
        }
      },
      { merge: true }
    );

    const cached = this.__memoCache.get(this.__getCacheKey(userId, classId, memberGroup));
    if (cached) {
      this.__setMemoCacheEntry(cached, assignmentKey, studentId, entry);
    }
  },

  async batchSaveMemos(
    userId: string,
    classId: string,
    updates: Array<{
      assignmentKey: string;
      studentId: string;
      memo: string;
      studentName?: string;
      studentNumber?: string;
    }>,
    memberGroup?: string
  ): Promise<void> {
    // 빈 메모는 삭제, 유효한 메모는 저장
    const toDelete = updates.filter(u => !u.memo || !u.memo.trim());
    const validUpdates = updates.filter(u => u.memo && u.memo.trim());

    // 빈 메모 삭제
    for (const u of toDelete) {
      await this.deleteMemo(userId, classId, u.assignmentKey, u.studentId, memberGroup);
    }

    if (!validUpdates.length) return;
    
    const collectionId = this.__getCollectionId(userId, classId, memberGroup);
    const memoRef = doc(db, 'users', userId, 'classes', classId, collectionId, ASSIGNMENT_MEMO_DOC_ID);
    
    // 중첩 객체로 assignments 구성
    const assignments: Record<string, Record<string, AssignmentMemoEntry>> = {};

    for (const update of validUpdates) {
      const entry: AssignmentMemoEntry = {
        studentId: update.studentId,
        memo: update.memo.trim(),
        updatedAt: new Date(),
        ...(update.studentName ? { studentName: update.studentName } : {}),
        ...(update.studentNumber ? { studentNumber: update.studentNumber } : {})
      };
      
      if (!assignments[update.assignmentKey]) {
        assignments[update.assignmentKey] = {};
      }
      assignments[update.assignmentKey][update.studentId] = entry;
    }

    await setDoc(
      memoRef,
      {
        classId,
        lastUpdated: new Date(),
        assignments
      },
      { merge: true }
    );

    const cacheKey = this.__getCacheKey(userId, classId, memberGroup);
    const cached = this.__memoCache.get(cacheKey);
    if (cached) {
      for (const u of toDelete) {
        this.__deleteMemoCacheEntry(cached, u.assignmentKey, u.studentId);
      }
      for (const update of validUpdates) {
        const entry: AssignmentMemoEntry = {
          studentId: update.studentId,
          memo: update.memo.trim(),
          updatedAt: new Date(),
          ...(update.studentName ? { studentName: update.studentName } : {}),
          ...(update.studentNumber ? { studentNumber: update.studentNumber } : {})
        };
        this.__setMemoCacheEntry(cached, update.assignmentKey, update.studentId, entry);
      }
    }
  },

  /**
   * 플랫 키 구조(assignments.xxx.yyy)를 중첩 객체 구조로 마이그레이션
   * 예: "assignments.general_123.member-456" → assignments.general_123.member-456 (중첩)
   */
  async migrateFlatKeysToNested(
    userId: string,
    classId: string,
    memberGroup?: string
  ): Promise<{ migrated: number; deleted: number }> {
    const collectionId = this.__getCollectionId(userId, classId, memberGroup);
    const memoRef = doc(db, 'users', userId, 'classes', classId, collectionId, ASSIGNMENT_MEMO_DOC_ID);
    const memoSnap = await getDoc(memoRef);
    
    if (!memoSnap.exists()) {
      return { migrated: 0, deleted: 0 };
    }

    const rawData = memoSnap.data() as Record<string, any>;
    
    // 플랫 키 찾기: "assignments.xxx.yyy" 형태
    const flatKeyPattern = /^assignments\.([^.]+)\.([^.]+)$/;
    const flatKeys: string[] = [];
    const nestedAssignments: Record<string, Record<string, any>> = {};

    for (const key of Object.keys(rawData)) {
      const match = key.match(flatKeyPattern);
      if (match) {
        const [, assignmentKey, studentId] = match;
        flatKeys.push(key);
        
        if (!nestedAssignments[assignmentKey]) {
          nestedAssignments[assignmentKey] = {};
        }
        nestedAssignments[assignmentKey][studentId] = rawData[key];
      }
    }

    if (flatKeys.length === 0) {
      return { migrated: 0, deleted: 0 };
    }

    // 삭제할 플랫 키와 새 중첩 구조를 함께 저장
    const updatePayload: Record<string, any> = {
      lastUpdated: new Date(),
      assignments: nestedAssignments
    };

    // 플랫 키 삭제
    for (const flatKey of flatKeys) {
      updatePayload[flatKey] = deleteField();
    }

    await setDoc(memoRef, updatePayload, { merge: true });
    this.invalidateMemoCache(userId, classId, memberGroup);

    return { migrated: flatKeys.length, deleted: flatKeys.length };
  }
};
